import {
  Layer,
  Space,
  SpaceChildrenItem,
  SpaceDelimitationGeoStandard,
  Time,
} from './spaceTime.types'
import {
  currentOnlyTimeTypes,
  generateIncidenceDates,
  incidenceTimeTypes,
  prevalenceTimeTypes,
  timeTypeForIncidence,
} from '~/prix/types/presets/time'
import { stringifyDate } from '~/prix/types/date'
import {
  childrenItemViewLabels,
  compatibleSpaceChildren,
  spaceTypePluralLabels,
} from './spaceTime.data'
import { Variable } from './variables.typing'

function resolveChildrenTitle(
  childrenType: Space['childrenType'],
  childrenItemView?: SpaceChildrenItem['childrenItemView'],
  currentTitle?: string,
): string {
  if (currentTitle) {
    return currentTitle
  }

  if (childrenType === 'item' && childrenItemView && childrenItemView !== 'item') {
    return childrenItemViewLabels[childrenItemView]
  }

  return spaceTypePluralLabels[childrenType]
}

export function getDefaultTimeForVariable(variable: Variable): Time {
  if (variable.timeType === 'currentOnly') {
    return {
      type: 'current',
      title: 'Atual',
    }
  } else if (variable.timeType === 'prevalence') {
    return {
      type: 'current',
      title: 'Atual',
    }
  } else if (variable.timeType === 'incidence') {
    const type = 'currentYearSubOneDay'
    const [startDate, endDate] = generateIncidenceDates[type]()
    const [newStartDate, newEndDate] = [stringifyDate(startDate), stringifyDate(endDate)]
    return {
      type,
      title: timeTypeForIncidence.keysAndLabels[type],
      startDate: newStartDate,
      endDate: newEndDate,
    }
  } else {
    throw new Error('Invalid time type')
  }
}

function currentTimeIfCompatible(variable: Variable, current?: Layer): Time | null {
  if (!current) {
    return null
  }

  if (variable.timeType === 'incidence' && incidenceTimeTypes.includes(current.time.type)) {
    return current.time
  }
  if (variable.timeType === 'currentOnly' && currentOnlyTimeTypes.includes(current.time.type)) {
    return current.time
  }
  if (variable.timeType === 'prevalence' && prevalenceTimeTypes.includes(current.time.type)) {
    return current.time
  }
  return null
}

export function getDefaultSpaceForVariable({
  variable,
  hasPermissions,
  stateId,
  stateName,
}: {
  variable: Variable
  hasPermissions: boolean | undefined
  stateId: number | null
  stateName: string | null
}): Space {
  if (hasPermissions) {
    const space = {
      childrenType: 'state',
      childrenTitle: spaceTypePluralLabels.state,
      delimitationId: 30,
      delimitationTitle: 'Brasil',
      delimitationType: 'country',
    } as Space

    return space
  }

  const delimitationType = 'state'
  const allCompatibleChildrenTypes = variable.compatibleChildrenTypes
    ? compatibleSpaceChildren[delimitationType].filter(type =>
        variable.compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[delimitationType]
  const childrenType = allCompatibleChildrenTypes[0]
  const space: Space = {
    delimitationTitle: stateName,
    delimitationType,
    delimitationId: stateId,
    childrenType: childrenType,
    childrenTitle: resolveChildrenTitle(childrenType),
  } as Space

  return space
}

function currentSpaceIfCompatible(variable: Variable, current?: Layer): Space | null {
  if (!current) {
    return null
  }

  const delimitationType = current.space.delimitationType
  const allCompatibleChildrenTypes = variable.compatibleChildrenTypes
    ? compatibleSpaceChildren[delimitationType].filter(type =>
        variable.compatibleChildrenTypes!.includes(type),
      )
    : compatibleSpaceChildren[delimitationType]
  if (!allCompatibleChildrenTypes.length) {
    return null
  }
  const childrenType = allCompatibleChildrenTypes.includes(current.space.childrenType)
    ? current.space.childrenType
    : allCompatibleChildrenTypes[0]

  const space: Space = {
    delimitationTitle: current.space.delimitationTitle,
    delimitationType: current.space.delimitationType,
    delimitationId: (current.space as SpaceDelimitationGeoStandard).delimitationId,
    childrenType,
    childrenItemView: (current.space as SpaceChildrenItem).childrenItemView ?? undefined,
    childrenTitle: resolveChildrenTitle(
      childrenType,
      (current.space as SpaceChildrenItem).childrenItemView ?? undefined,
      current.space.childrenType === childrenType ? current.space.childrenTitle : undefined,
    ),
  } as Space
  return space
}

function recommendedSpaceIfCompatible(variable: Variable): Space | null {
  if (!variable.recommendedSpaces || variable.recommendedSpaces.length === 0) {
    return null
  }

  for (const recommendedSpace of variable.recommendedSpaces) {
    const delimitationType = recommendedSpace.delimitationType
    const allCompatibleChildrenTypes = variable.compatibleChildrenTypes
      ? compatibleSpaceChildren[delimitationType].filter(type =>
          variable.compatibleChildrenTypes!.includes(type),
        )
      : compatibleSpaceChildren[delimitationType]
    if (!allCompatibleChildrenTypes.length) {
      continue
    }
    const childrenType = allCompatibleChildrenTypes.includes(recommendedSpace.childrenType)
      ? recommendedSpace.childrenType
      : allCompatibleChildrenTypes[0]
    const space: Space = {
      delimitationTitle: recommendedSpace.delimitationTitle,
      delimitationType: recommendedSpace.delimitationType,
      delimitationId: (recommendedSpace as SpaceDelimitationGeoStandard).delimitationId,
      childrenType,
      childrenItemView: (recommendedSpace as SpaceChildrenItem).childrenItemView ?? undefined,
      childrenTitle: resolveChildrenTitle(
        childrenType,
        (recommendedSpace as SpaceChildrenItem).childrenItemView ?? undefined,
        recommendedSpace.childrenType === childrenType ? recommendedSpace.childrenTitle : undefined,
      ),
    } as Space

    console.log('Using recommended space for variable:', variable.presetKey, space)
    return space
  }

  return null
}

export default function recommendedLayerForVariable(
  variable: Variable,
  hasPermissions: boolean | undefined,
  stateId: number | null,
  stateName: string | null,
  current?: Layer,
) {
  const time: Time =
    currentTimeIfCompatible(variable, current) ?? getDefaultTimeForVariable(variable)

  const space: Space =
    stateId && stateName
      ? getDefaultSpaceForVariable({ variable, hasPermissions: false, stateId, stateName })
      : (recommendedSpaceIfCompatible(variable) ??
        currentSpaceIfCompatible(variable, current) ??
        getDefaultSpaceForVariable({ variable, hasPermissions, stateId, stateName }))

  return {
    variable,
    space,
    time,
  }
}
